import { toast } from "@/hooks/use-toast"
import { isElectron as isElectronEnv } from '../utils/electron';
import type { ElectronAPI } from '../types/electron';
import type { Patient } from '@/types';
import { getAuthStorage, setAuthStorage, clearAuthStorage } from '@/lib/authStorage';

// Declare the window.electron interface for Electron
declare global {
  interface Window {
    electron?: {
      auth: {
        login: (email: string, password: string) => Promise<any>;
        logout: () => Promise<void>;
        check: () => Promise<any>;
      };
      patients: {
        getAll: () => Promise<any[]>;
        getById: (id: string) => Promise<any>;
        create: (data: any) => Promise<any>;
        update: (id: string, data: any) => Promise<any>;
        delete: (id: string) => Promise<any>;
        search: (query: string) => Promise<any[]>;
        getRecent: (limit: number) => Promise<any[]>;
      };
      appointments: {
        getAll: () => Promise<any[]>;
        getById: (id: string) => Promise<any>;
        create: (data: any) => Promise<any>;
        update: (id: string, data: any) => Promise<any>;
        delete: (id: string) => Promise<any>;
        getAllAppointments: (tenant_id: string, clinic_id: string) => Promise<any[]>;
        getUpcoming: (limit: number, doctor_id?: string, clinic_id?: string) => Promise<any[]>;
      };
      prescriptions: {
        getAll: () => Promise<any[]>;
        create: (data: any) => Promise<any>;
      };
      templates: {
        getAll: (type: string, doctorId: string, clinicId: string) => Promise<any[]>;
        create: (data: any) => Promise<any>;
      };
      system: {
        getBackendPort: () => Promise<number>;
      };
    };
  }
}

// Enhanced error handling types
interface ApiError {
  message: string;
  status?: number;
  code?: string;
  details?: any;
}

// Create a more specific error class
class ApiRequestError extends Error {
  public status?: number;
  public code?: string;
  public details?: any;

  constructor(message: string, status?: number, code?: string, details?: any) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// Error codes returned by the backend's license/plan gating (see
// backend/middleware/license.js and backend/middleware/requirePermission.js).
// LICENSE_EXPIRED/LICENSE_INACTIVE/LICENSE_INVALID are account-wide and shown
// via a persistent banner; the rest are action-specific and shown as toasts
// by the calling component using err.message.
const ACCOUNT_LICENSE_ERROR_CODES = ['LICENSE_EXPIRED', 'LICENSE_INACTIVE', 'LICENSE_INVALID'];
export const LICENSE_STATUS_EVENT = 'healthbuddy:license-status';

// Dynamic backend URL - will be updated when app starts
let BASE_URL = import.meta.env.VITE_API_BASE_URL || 
  (import.meta.env.MODE === 'production' 
    ? '/api'
    : 'http://localhost:3001');
let portDiscoveryPromise: Promise<void> | null = null;

// Function to discover and update the backend port
async function discoverBackendPort(): Promise<void> {
  if (!isElectronEnv() || !window.electron?.system?.getBackendPort) {
    return; // Not in Electron or API not available
  }

  try {
    const port = await window.electron.system.getBackendPort();
    if (port && port !== 3001) {
      BASE_URL = `http://localhost:${port}`;
      console.log(`🔗 Updated API base URL to: ${BASE_URL}`);
    }
  } catch (error: any) {
    console.warn("Could not get backend port from Electron, using default:", error);
  }
}

// Exported so other API modules can build URLs using the live base URL
export function getBaseUrl(): string { return BASE_URL; }

// Lazy initialization of backend port discovery
function ensureBackendPortDiscovered(): Promise<void> {
  if (!portDiscoveryPromise) {
    portDiscoveryPromise = discoverBackendPort();
  }
  return portDiscoveryPromise;
}

// Helper to determine if we should use Electron IPC
function shouldUseElectronAPI(endpoint: string): boolean {
  if (!isElectronEnv() || !window.electron) return false;
  
  // Map HTTP endpoints to Electron IPC endpoints
  const endpointMap: Record<string, string> = {
    '/patients': 'patients.getAll',
    '/patients/search': 'patients.search',
    '/appointments': 'appointments.getAll',
    '/prescriptions': 'prescriptions.getAll',
    '/templates': 'templates.getAll'
  };

  return !!endpointMap[endpoint];
}

// Helper to get the Electron IPC method
function getElectronMethod(endpoint: string, method: string = 'GET'): string | null {
  if (!isElectronEnv() || !window.electron) return null;

  // Map HTTP endpoints and methods to Electron IPC methods
  const methodMap: Record<string, Record<string, string>> = {
    '/patients': { 
      GET: 'patients.getAll',
      POST: 'patients.create'
    },
    '/patients/search': { GET: 'patients.search' },
    '/appointments': {
      GET: 'appointments.getAll',
      POST: 'appointments.create'
    },
    '/prescriptions': {
      GET: 'prescriptions.getAll',
      POST: 'prescriptions.create'
    },
    '/templates': {
      GET: 'templates.getAll',
      POST: 'templates.create'
    }
  };

  return methodMap[endpoint]?.[method] || null;
}

// Add error handling for CORS and CSP issues
const handleFetchError = (error: any, customError: string) => {
  console.error('API request failed:', error);
  
  if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
    const isCORSError = error.message.includes('CORS') || error.message.includes('Content Security Policy');
    if (isCORSError) {
      throw new ApiRequestError(
        'Unable to connect to server due to security policy. Please check your network connection or contact support.',
        0,
        'CORS_ERROR'
      );
    }
  }
  
  // Provide user-friendly message for server errors — but only when the backend
  // didn't already send a specific one (e.g. errorData.error from a validation/DB failure).
  let userMessage = error.message || customError;
  const hasBackendMessage = !!(error.details?.error || error.details?.message);
  if (!hasBackendMessage && (error.status === 500 || error.status === 502 || error.status === 503 || error.status === 504)) {
    userMessage = 'Server error. Please try again in a moment.';
  } else if (error.status === 0 || error.message === 'Failed to fetch') {
    userMessage = 'Network error. Please check your connection.';
  }
  
  throw new ApiRequestError(
    userMessage,
    error.status,
    error.code,
    error.details
  );
};

// Helper to get auth token (from session or local storage per "Remember me")
function getAuthToken(): string | null {
  try {
    return getAuthStorage().getItem('accessToken');
  } catch {
    return null;
  }
}

// Helper for safe fetch with enhanced error handling
export async function safeFetch<T>(
  url: string, 
  options?: RequestInit, 
  retries = 1,
  customError = "Something went wrong"
): Promise<T | null> { 
  // Extract the endpoint from the URL
  const endpoint = url.replace(BASE_URL, '');
  
  // Check if we should use Electron IPC
  if (shouldUseElectronAPI(endpoint)) {
    try {
      const method = getElectronMethod(endpoint, options?.method || 'GET');
      if (method) {
        const [namespace, func] = method.split('.');
        const data = options?.body ? JSON.parse(options.body as string) : undefined;
        // @ts-ignore - Dynamic access
        const result = await window.electron[namespace][func](data);
        return result as T;
      }
    } catch (error: any) {
      handleFetchError(error, customError);
    }
  }

  // Fallback to HTTP request
  await ensureBackendPortDiscovered();
  
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const timeoutMs = Number(import.meta.env.VITE_API_REQUEST_TIMEOUT_MS) || 30000;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      
      // Get auth token and add to headers
      const token = getAuthToken();
      const authHeaders: Record<string, string> = {};
      if (token) {
        authHeaders['Authorization'] = `Bearer ${token}`;
      }
      
      const fetchOptions: RequestInit = {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...authHeaders,
          ...options?.headers,
        }
      };

      const res = await fetch(url, fetchOptions);
      clearTimeout(timeoutId);

      if (res.status === 401) {
        const isRefreshEndpoint = url.includes('/auth/refresh');
        const isLoginEndpoint = url.includes('/auth/login');
        
        // Don't auto-redirect on login failures; let the component handle the error
        if (isLoginEndpoint) {
          // Let it fall through to error handling below
        } else if (!isRefreshEndpoint && (await refreshAccessToken())) {
          const newToken = getAuthToken();
          if (newToken) {
            const retryOptions: RequestInit = {
              ...options,
              headers: {
                ...options?.headers,
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                Authorization: `Bearer ${newToken}`,
              },
            };
            const retryRes = await fetch(url, retryOptions);
            if (retryRes.ok) {
              const contentType = retryRes.headers.get("Content-Type") || "";
              if (contentType.includes("application/json")) {
                return await retryRes.json();
              }
            }
          }
        } else if (!isLoginEndpoint) {
          // Only redirect to login for non-login endpoints (session expired, etc)
          clearAuthStorage();
          window.location.href = '/login?reason=expired';
          return null;
        }
      }

      if (!res.ok) {
        let errorMessage = `HTTP ${res.status}: ${res.statusText}`;
        let errorCode = 'HTTP_ERROR';
        let errorDetails: any = null;

        try {
          const errorData = await res.json();
          errorDetails = errorData;
          // License/plan gating responses use { error: '<CODE>', message: '<human text>' }
          if (res.status === 402 && errorData.error && errorData.message) {
            errorCode = errorData.error;
            errorMessage = errorData.message;
          } else {
            errorMessage = errorData.error || errorData.message || errorMessage;
          }
        } catch {
          // If response isn't JSON, use status text
        }

        if (ACCOUNT_LICENSE_ERROR_CODES.includes(errorCode)) {
          window.dispatchEvent(new CustomEvent(LICENSE_STATUS_EVENT, {
            detail: { code: errorCode, message: errorMessage },
          }));
        }

        throw new ApiRequestError(errorMessage, res.status, errorCode, errorDetails);
      }
      
      const contentType = res.headers.get("Content-Type") || "";
      if (!contentType.includes("application/json")) {
        throw new ApiRequestError("Server returned invalid response format", 502, 'INVALID_RESPONSE');
      }
      
      const data = await res.json();
      return data;
      
    } catch (err: any) {
      lastError = err;
      
      if (err instanceof ApiRequestError) {
        if (err.status && [400, 401, 402, 403, 404, 422].includes(err.status)) {
          break; // Don't retry client errors
        }
      }
      
      if (attempt === retries) {
        break;
      }
      
      const delay = Math.min(1000 * Math.pow(2, attempt), 5000);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  if (lastError) {
    handleFetchError(lastError, customError);
  }
  
  return null;
}

// ------------------ Patients ------------------

export async function getPatients(options?: {
  tenant_id?: string;
  clinic_id: string;
  search?: string;
  gender?: string;
  sortField?: 'name' | 'age' | 'created_at';
  sortOrder?: 'asc' | 'desc';
  page?: number | string;
  pageSize?: number | string;
}): Promise<{ patients: Patient[]; total: number }> {
  if (isElectronEnv() && window.electron?.patients?.getAll) {
    try {
      console.log('Getting patients via IPC...');
      const data = await window.electron.patients.getAll();
      console.log('Got patients via IPC');
      return { patients: data, total: data.length };
    } catch (error: any) {
      console.error('Error getting patients via IPC:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to get patients",
        variant: "destructive",
      });
      throw error;
    }
  }

  // clinic_id is required; doctor_id is optional (receptionists don't have one)
  if (!options || !options.clinic_id) {
    throw new Error('clinic_id is required');
  }

  const params = new URLSearchParams();
  params.append('clinic_id', options.clinic_id);
  if (options.search) params.append('search', options.search);
  if (options.gender) params.append('gender', options.gender);
  if (options.sortField) params.append('sortField', options.sortField);
  if (options.sortOrder) params.append('sortOrder', options.sortOrder);
  if (options.page) params.append('page', String(options.page));
  if (options.pageSize) params.append('pageSize', String(options.pageSize));

  const response = await safeFetch<{ patients: Patient[]; total: number }>(`${BASE_URL}/patients?${params.toString()}`);
  if (response && Array.isArray(response.patients) && typeof response.total === 'number') {
    return response;
  }
  return { patients: [], total: 0 };
}

export async function getRecentPatients(limit = 5, doctor_id?: string, clinic_id?: string) {
  if (isElectronEnv() && window.electron?.patients?.getRecent) {
    return window.electron.patients.getRecent(limit);
  }
  
  const params = new URLSearchParams();
  params.append('limit', limit.toString());
  params.append('sort', 'desc');
  if (doctor_id) params.append('doctor_id', doctor_id);
  if (clinic_id) params.append('clinic_id', clinic_id);
  
  const response = await safeFetch<{ patients: any[]; total: number }>(`${BASE_URL}/patients?${params.toString()}`);
  
  // Handle the response structure: { patients: Patient[], total: number }
  if (response && response.patients && Array.isArray(response.patients)) {
    return response.patients;
  }
  
  // Fallback for direct array response
  if (response && Array.isArray(response)) {
    return response;
  }
  
  return [];
}

export async function searchPatients(query: string, clinic_id?: string) {
  if (isElectronEnv() && window.electron?.patients?.search) {
    return window.electron.patients.search(query);
  }
  const params = new URLSearchParams({ q: query });
  if (clinic_id) params.append('clinic_id', clinic_id);
  return safeFetch<any[]>(`${BASE_URL}/patients/search?${params.toString()}`);
}

export async function addPatient(data: {
  name: string
  age: number | null
  gender: string
  phone: string
  date_of_birth?: string | null
  phone_2?: string | null
  email?: string | null
  address?: string | null
  city?: string | null
  pin_code?: string | null
  preferred_language?: string
  aadhar_number?: string | null
  blood_group?: string | null
  allergies?: string | null
  chronic_conditions?: string | null
  current_medications?: string | null
  emergency_contact_name?: string | null
  emergency_contact_phone?: string | null
  payment_preference?: string
  doctor_id?: string
  clinic_id?: string
}) {
  if (isElectronEnv() && window.electron?.patients?.create) {
    return window.electron.patients.create(data);
  }
  return safeFetch<any>(`${BASE_URL}/patients`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

export async function updatePatient(patientId: string, data: {
  name?: string
  age?: number | null
  gender?: string
  phone?: string
  date_of_birth?: string | null
  phone_2?: string | null
  email?: string | null
  address?: string | null
  city?: string | null
  pin_code?: string | null
  preferred_language?: string
  aadhar_number?: string | null
  blood_group?: string | null
  allergies?: string | null
  chronic_conditions?: string | null
  current_medications?: string | null
  emergency_contact_name?: string | null
  emergency_contact_phone?: string | null
  payment_preference?: string
  doctor_id?: string
  clinic_id?: string
}) {
  return safeFetch<any>(`${BASE_URL}/patients/${patientId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

export async function getProfile(patientId: string) {
  return safeFetch<any>(`${BASE_URL}/patients/profile/${patientId}`)
}

export async function getPatient(patientId: string) {
  return safeFetch<any>(`${BASE_URL}/patients/${patientId}`)
}

// ------------------ Prescriptions ------------------

export async function getPrescriptions(params?: { doctor_id?: string; patient_id?: string; clinic_id?: string }) {
  if (isElectronEnv() && window.electron?.prescriptions?.getAll) {
    return window.electron.prescriptions.getAll();
  }
  const query = new URLSearchParams();
  if (params?.doctor_id) query.set('doctor_id', params.doctor_id);
  if (params?.patient_id) query.set('patient_id', params.patient_id);
  if (params?.clinic_id)  query.set('clinic_id',  params.clinic_id);
  const qs = query.toString();
  return safeFetch<any[]>(`${BASE_URL}/prescriptions${qs ? `?${qs}` : ''}`);
}

export async function addPrescription(data: any) {
  if (isElectronEnv() && window.electron?.prescriptions?.create) {
    return window.electron.prescriptions.create(data);
  }
  return safeFetch<any>(`${BASE_URL}/prescriptions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

export async function updatePrescription(data: any) {
  const { id, ...body } = data; // Extract id for URL, remove from body
  return safeFetch<any>(`${BASE_URL}/prescriptions/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body)
  });
}

export async function getPrescriptionsByPatient(patientId: string) {
  return safeFetch<any[]>(`${BASE_URL}/prescriptions/patient/${patientId}`)
}

export async function getPrescriptionById(prescriptionId: string) {
  return safeFetch<any>(`${BASE_URL}/prescriptions/${prescriptionId}`)
}

// ------------------ Appointments ------------------

export async function getAppointments() {
  if (isElectronEnv() && window.electron?.appointments?.getAll) {
    return window.electron.appointments.getAll();
  }
  return safeFetch<any[]>(`${BASE_URL}/appointments`);
}

export async function getUpcomingAppointments(limit = 5, doctor_id?: string, clinic_id?: string) {
  if (isElectronEnv() && window.electron?.appointments?.getUpcoming) {
    return window.electron.appointments.getUpcoming(limit);
  }
  
  const params = new URLSearchParams();
  params.append('limit', limit.toString());
  if (doctor_id) params.append('doctor_id', doctor_id);
  if (clinic_id) params.append('clinic_id', clinic_id);
  
  return safeFetch<any[]>(`${BASE_URL}/appointments/upcoming?${params.toString()}`)
}

export async function scheduleAppointment(appt: any) {
  return safeFetch<any>(`${BASE_URL}/appointments`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(appt),
  })
}

export async function addAppointment(data: any) {
  if (isElectronEnv() && window.electron?.appointments?.create) {
    return window.electron.appointments.create(data);
  }
  return safeFetch<any>(`${BASE_URL}/appointments`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

export async function updateAppointment(data: {
  id?: string
  patient_id?: string
  date?: string
  time?: string
  reason?: string
  status?: string
  updated_at?: string
}) {
  return safeFetch<any>(`${BASE_URL}/appointments/${data.id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  })
}

export async function deleteAppointment(appointmentId: string) {
  return safeFetch<any>(`${BASE_URL}/appointments/${appointmentId}`, {
    method: "DELETE",
  })
}

export async function getAppointmentsByDateRange(startDate: string, endDate: string, clinicId?: string, doctorId?: string) {
  const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
  if (clinicId) params.set('clinic_id', clinicId);
  if (doctorId) params.set('doctor_id', doctorId);
  return safeFetch<any[]>(`${BASE_URL}/appointments/range?${params.toString()}`);
}

export async function getAllAppointments(tenant_id: string, clinic_id: string) {
  if (isElectronEnv() && window.electron?.appointments?.getAllAppointments) {
    try {
      console.log('Getting all appointments via IPC...');
      const data = await window.electron.appointments.getAllAppointments(tenant_id, clinic_id);
      console.log('Got all appointments via IPC:', data);
      return data;
    } catch (error: any) {
      console.error('Error getting all appointments via IPC:', error);
      throw new Error(error.message || "Failed to get all appointments");
    }
  }
  return safeFetch<any[]>(`${BASE_URL}/appointments/all`);
}

// ------------------ Queue ------------------

export interface QueueEntry {
  id: string;
  tokenNumber: number;
  sortOrder: number;
  patientName: string;
  patientPhone: string | null;
  patientId: string | null;
  appointmentId: string | null;
  status: 'waiting' | 'in_consultation' | 'completed' | 'cancelled';
  isPriority: boolean;
  checkedInAt: string;
  consultationStartAt: string | null;
  consultationEndAt: string | null;
  waitingTimeMinutes?: number | null;
}

export interface QueueTodayResponse {
  doctorId: string;
  clinicId: string;
  queueDate: string;
  summary: { totalWaiting: number; totalInConsultation: number; totalCompleted: number; totalCancelled: number };
  entries: QueueEntry[];
}

export async function getQueueToday(doctorId: string, clinicId: string) {
  const params = new URLSearchParams({ doctorId, clinicId });
  return safeFetch<QueueTodayResponse>(`${BASE_URL}/queue/today?${params.toString()}`);
}

export async function queueCheckin(appointmentId: string) {
  return safeFetch<QueueEntry>(`${BASE_URL}/queue/checkin`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ appointmentId }),
  });
}

export async function queueWalkin(
  patientId: string,
  doctorId: string,
  clinicId: string,
  isPriority: boolean = false
): Promise<QueueEntry> {
  return safeFetch<QueueEntry>(`${BASE_URL}/queue/walkin`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ patientId, doctorId, clinicId, isPriority }),
  });
}

export async function queueWalkinRegister(data: {
  name: string;
  phone: string;
  age?: number;
  gender?: string;
  doctorId: string;
  clinicId: string;
  isPriority?: boolean;
}): Promise<{ queueEntry: QueueEntry; patientCreated: boolean; patientId: string }> {
  return safeFetch<{ queueEntry: QueueEntry; patientCreated: boolean; patientId: string }>(
    `${BASE_URL}/queue/walkin/register`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }
  );
}

export interface QueueMyResponse {
  doctorId: string;
  clinicId: string;
  queueDate: string;
  summary: { totalWaiting: number; totalInConsultation: number };
  entries: QueueEntry[];
}

export async function getQueueMy(date?: string) {
  const params = date ? `?date=${encodeURIComponent(date)}` : '';
  return safeFetch<QueueMyResponse>(`${BASE_URL}/queue/my${params}`);
}

export async function queueStartConsultation(queueId: string) {
  return safeFetch<QueueEntry>(`${BASE_URL}/queue/${queueId}/start`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
  });
}

export async function queueCompleteConsultation(queueId: string) {
  return safeFetch<QueueEntry>(`${BASE_URL}/queue/${queueId}/complete`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
  });
}

/** Toggle priority: true = move to front, false = move to end. Returns updated full queue. */
export async function queueReorder(queueId: string, isPriority: boolean) {
  return safeFetch<QueueTodayResponse>(`${BASE_URL}/queue/${queueId}/reorder`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ isPriority }),
  });
}

/** Mark patient as left (cancel from queue). Only waiting entries. */
export async function queueCancel(queueId: string) {
  return safeFetch<QueueEntry>(`${BASE_URL}/queue/${queueId}/cancel`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
  });
}

// ------------------ Invoices ------------------

// Legacy invoice functions removed — use getBillingInvoices, createBillingInvoice, getPatientInvoices

// ------------------ Templates ------------------

// Get templates with enhanced filtering and search
export async function getTemplateSections(
  type: string, 
  doctor_id: string, 
  clinic_id: string,
  options?: {
    search?: string;
    category?: 'personal' | 'clinic' | 'shared';
    favorites_only?: boolean;
    limit?: number;
    offset?: number;
  }
) {
  const params = new URLSearchParams({
    type: encodeURIComponent(type),
    doctor_id: encodeURIComponent(doctor_id),
    clinic_id: encodeURIComponent(clinic_id)
  });

  // Add optional parameters
  if (options?.search) params.append('search', options.search);
  if (options?.category) params.append('category', options.category);
  if (options?.favorites_only) params.append('favorites_only', 'true');
  if (options?.limit) params.append('limit', options.limit.toString());
  if (options?.offset) params.append('offset', options.offset.toString());

  
  return safeFetch<any>(`${BASE_URL}/templates?${params.toString()}`);
}

// Get all templates for management page
export async function getAllTemplates(
  doctor_id: string, 
  clinic_id: string,
  options?: {
    search?: string;
    type?: string;
    category?: string;
    favorites_only?: boolean;
    limit?: number;
    offset?: number;
    sort_by?: string;
    sort_direction?: string;
    tags?: string[];
  }
) {
  const params = new URLSearchParams({
    doctor_id: encodeURIComponent(doctor_id),
    clinic_id: encodeURIComponent(clinic_id)
  });

  // Add optional parameters
  if (options?.search) params.append('search', options.search);
  if (options?.type) params.append('type', options.type);
  if (options?.category) params.append('category', options.category);
  if (options?.favorites_only) params.append('favorites_only', 'true');
  if (options?.limit) params.append('limit', options.limit.toString());
  if (options?.offset) params.append('offset', options.offset.toString());
  // Note: sort_by and sort_direction are handled in frontend
  // tags filtering is also handled in frontend

  
  // Use the correct endpoint - /templates (not /templates/all)
  const response = await safeFetch(`${BASE_URL}/templates?${params.toString()}`);
  
  // The backend returns { templates: [...], pagination: {...} }
  // So we return the full response structure
  return response;
}

// Get template statistics for dashboard
export async function getTemplateStats(doctor_id: string, clinic_id: string) {
  
  return safeFetch<any>(`${BASE_URL}/templates/stats?doctor_id=${encodeURIComponent(doctor_id)}&clinic_id=${encodeURIComponent(clinic_id)}`);
}

// Create new template
export async function addTemplateSection(data: {
  id?: string;
  doctor_id: string;
  clinic_id: string;
  title: string;
  description?: string;
  type: 'complaint' | 'medicine' | 'investigation' | 'advice' | 'diagnosis' | 'quick_notes';
  value: any;
  tags?: string[] | string;
  category?: 'personal' | 'clinic' | 'shared';
  is_global?: boolean;
  is_favorite?: boolean;
  created_by?: string;
}) {
  
  return safeFetch<any>(`${BASE_URL}/templates`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data), // Single template, not array
  });
}

// Update existing template
export async function updateTemplateSection(
  templateId: string, 
  data: {
    doctor_id: string;
    title?: string;
    description?: string;
    type?: string;
    value?: any;
    tags?: string[] | string;
    category?: string;
    is_favorite?: boolean;
  }
) {
  
  return safeFetch<any>(`${BASE_URL}/templates/${templateId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

// Delete template (soft delete)
export async function deleteTemplateSection(templateId: string, doctor_id: string) {
  
  return safeFetch<any>(`${BASE_URL}/templates/${templateId}?doctor_id=${encodeURIComponent(doctor_id)}`, {
    method: "DELETE",
  });
}

// Toggle favorite status
export async function toggleTemplateFavorite(
  templateId: string, 
  doctor_id: string, 
  is_favorite: boolean
) {
  
  return safeFetch<any>(`${BASE_URL}/templates/${templateId}/favorite`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ doctor_id, is_favorite }),
  });
}

// Track template usage
export async function trackTemplateUsage(
  templateId: string, 
  doctor_id: string,
  context?: string,
  patient_id?: string
) {
  
  return safeFetch<any>(`${BASE_URL}/templates/${templateId}/use`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ 
      doctor_id, 
      context: context || 'prescription_form',
      patient_id 
    }),
  });
}

// Batch create templates (keeping for backward compatibility)
export async function syncTemplateSections(batch: any[]) {
  return safeFetch<any>(`${BASE_URL}/templates`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(batch),
  });
}

// Duplicate template
export async function duplicateTemplate(
  templateId: string, 
  doctor_id: string, 
  newTitle?: string
) {
  
  // Get original template first
  const original = await safeFetch(`${BASE_URL}/templates?doctor_id=${doctor_id}&clinic_id=dummy`) as any;
  
  if (original?.templates) {
    const template = original.templates.find((t: any) => t.id === templateId);
    if (template) {
      return addTemplateSection({
        ...template,
        id: undefined, // Let server generate new ID
        title: newTitle || `${template.title} (Copy)`,
        usage_count: 0,
        is_favorite: false,
      });
    }
  }
  
  throw new Error('Template not found');
}

// Export templates (for backup)
export async function exportTemplates(doctor_id: string, clinic_id: string, options?: {
  type?: string;
  category?: string;
  favorites_only?: boolean;
}) {
  
  const response = await getAllTemplates(doctor_id, clinic_id, { limit: 1000, ...options }) as any;
  
  // Handle the response structure: { templates: [...], pagination: {...} }
  if (response?.templates) {
    const exportData = {
      version: "1.0",
      exported_at: new Date().toISOString(),
      doctor_id,
      clinic_id,
      templates: response.templates
    };
    
    // Create and download file
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `templates-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    return { success: true, count: response.templates.length };
  }
  
  throw new Error('Failed to export templates');
}

// Import templates (from backup)
export async function importTemplates(
  file: File, 
  doctor_id: string, 
  clinic_id: string
) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const content = e.target?.result as string;
        const importData = JSON.parse(content);
        
        if (!importData.templates || !Array.isArray(importData.templates)) {
          throw new Error('Invalid import file format');
        }
        
        // Process templates for import
        const templatesToImport = importData.templates.map((template: any) => ({
          ...template,
          id: undefined, // Generate new IDs
          doctor_id,
          clinic_id,
          usage_count: 0, // Reset usage count
          created_at: undefined, // Set new creation time
          updated_at: undefined,
        }));
        
        // Import in batches
        const results = [];
        for (const template of templatesToImport) {
          try {
            const result = await addTemplateSection(template);
            results.push(result);
          } catch (error) {
            console.warn('Failed to import template:', template.title, error);
          }
        }
        
        resolve({ 
          success: true, 
          imported: results.length, 
          total: templatesToImport.length 
        });
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

// ------------------ Settings ------------------

const DEFAULT_PRESCRIPTION_LAYOUT = {
  hiddenSections: [] as string[],
  customFields: [
    { key: 'custom_1', label: '', enabled: false },
    { key: 'custom_2', label: '', enabled: false },
    { key: 'custom_3', label: '', enabled: false },
  ],
};

// GET /settings - Get user settings
export async function getSettings(doctor_id: string, clinic_id: string) {
  const params = new URLSearchParams({
    doctor_id: encodeURIComponent(doctor_id),
    clinic_id: encodeURIComponent(clinic_id)
  });

  const settings = await safeFetch<any>(`${BASE_URL}/settings?${params.toString()}`);
  
  // Transform flat backend response to nested frontend structure
  if (settings) {
    return {
      ...settings,
      profile: {
        name: settings.profile_name || '',
        email: settings.profile_email || '',
        phone: settings.profile_phone || '',
        specialty: settings.profile_specialty || 'General Medicine',
        license: settings.profile_license || '',
        signature: settings.profile_signature || ''
      },
      preferences: {
        theme: settings.theme || 'light',
        language: settings.language || 'en',
        notifications: settings.notifications !== undefined ? settings.notifications : true,
        autoSave: settings.auto_save !== undefined ? settings.auto_save : true,
        printFormat: settings.print_format || 'a4'
      },
      prescriptionLayout: settings.prescription_layout || DEFAULT_PRESCRIPTION_LAYOUT
    };
  }

  // Always return all keys with defaults if settings is null
  return {
    profile: {
      name: '', email: '', phone: '', specialty: 'General Medicine', license: '', signature: ''
    },
    preferences: {
      theme: 'light', language: 'en', notifications: true, autoSave: true, printFormat: 'a4'
    },
    prescriptionLayout: DEFAULT_PRESCRIPTION_LAYOUT
  };
}

// Update user settings (handles first-time creation)
export async function updateSettings(doctor_id: string, clinic_id: string, settings: any) {
  const data = {
    doctor_id,
    clinic_id,
    ...settings
  };

  
  return safeFetch<any>(`${BASE_URL}/settings`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

// Get database statistics
export async function getDatabaseStats(doctor_id: string, clinic_id: string) {
  const params = new URLSearchParams({
    doctor_id: encodeURIComponent(doctor_id),
    clinic_id: encodeURIComponent(clinic_id)
  });

  
  return safeFetch<any>(`${BASE_URL}/settings/stats?${params.toString()}`);
}

export async function getBillingServices(clinicId: string, doctorId?: string, category?: string, ipdOnly?: boolean) {
  let url = `${BASE_URL}/billing/services/${clinicId}`;
  const params = new URLSearchParams();

  if (doctorId) params.append('doctorId', doctorId);
  if (category) params.append('category', category);
  if (ipdOnly)  params.append('ipd_only', 'true');

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  return safeFetch(url);
}

export async function getBillingServiceById(serviceId: string) {
  return safeFetch<any>(`${BASE_URL}/billing/services/single/${serviceId}`);
}

export async function createBillingService(serviceData: {
  clinic_id: string;
  doctor_id?: string;
  name: string;
  price: number;
  category?: "other" | "consultation" | "procedure" | "diagnostic" | "investigation"
  is_active?: boolean;
  is_ipd_applicable?: boolean;
  sort_order?: number;
}) {
  return safeFetch<any>(`${BASE_URL}/billing/services`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(serviceData),
  });
}

export async function updateBillingService(serviceId: string, serviceData: {
  name: string;
  price: number;
  category?: string;
  is_active?: boolean;
  is_ipd_applicable?: boolean;
  sort_order?: number;
}) {
  return safeFetch<any>(`${BASE_URL}/billing/services/${serviceId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(serviceData),
  });
}

export async function deleteBillingService(serviceId: string) {
  return safeFetch<any>(`${BASE_URL}/billing/services/${serviceId}`, {
    method: "DELETE",
  });
}

export async function getBillingServiceStats(clinicId: string, doctorId?: string) {
  let url = `${BASE_URL}/billing/services/stats/${clinicId}`;
  if (doctorId) url += `?doctorId=${doctorId}`;
  
  return safeFetch(url);
}

export async function createDefaultBillingServices(clinicId: string, createdBy?: string) {
  return safeFetch<any>(`${BASE_URL}/billing/services/default/${clinicId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ created_by: createdBy }),
  });
}

// ------------------ Billing Invoices ------------------

// getBillingInvoices merged into getAllBillingInvoices below

export async function getBillingInvoicesByPatient(patientId: string) {
  return safeFetch<any>(`${BASE_URL}/billing/invoices/patient/${patientId}`);
}

export async function getBillingInvoiceById(invoiceId: string) {
  return safeFetch<any>(`${BASE_URL}/billing/invoices/single/${invoiceId}`);
}

export async function createBillingInvoice(invoiceData: {
  patient_id: string;
  doctor_id: string;
  clinic_id: string;
  invoice_date?: string;
  due_date?: string;
  items: Array<{
    service_id?: string;
    service_name: string;
    category?: string;
    quantity: number;
    unit_price: number;
    total_price?: number;
    notes?: string;
  }>;
  discount_amount?: number;
  discount_type?: 'flat' | 'percent';
  discount_reason?: string;
  tax_amount?: number;
  billing_name?: string;
  billing_gstin?: string;
  notes?: string;
  payments?: Array<{
    amount: number;
    payment_mode: string;
    reference_number?: string;
    upi_app?: string;
    bank_name?: string;
    payment_date?: string;
    notes?: string;
  }>;
  balance_forward?: number;
  balance_forward_settle?: number;
  balance_forward_payment_mode?: string;
  deposit_amount?: number;
  deposit_payment_mode?: string;
}) {
  return safeFetch<any>(`${BASE_URL}/billing/invoices`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(invoiceData),
  });
}

export async function updateBillingInvoice(invoiceId: string, invoiceData: {
  invoice_date?: string;
  due_date?: string;
  items?: Array<{
    service_id?: string;
    service_name: string;
    category?: string;
    quantity: number;
    unit_price: number;
    total_price?: number;
    notes?: string;
  }>;
  discount_amount?: number;
  discount_type?: string;
  discount_reason?: string;
  tax_amount?: number;
  billing_name?: string;
  billing_gstin?: string;
  notes?: string;
}) {
  return safeFetch<any>(`${BASE_URL}/billing/invoices/${invoiceId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(invoiceData),
  });
}

export async function recordInvoicePayment(invoiceId: string, paymentData: {
  amount: number;
  payment_mode: 'cash' | 'upi' | 'card' | 'netbanking' | 'cheque' | 'advance' | 'other';
  reference_number?: string;
  upi_app?: string;
  bank_name?: string;
  payment_date?: string;
  notes?: string;
}) {
  return safeFetch<any>(`${BASE_URL}/billing/invoices/${invoiceId}/payment`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(paymentData),
  });
}

export interface PaymentRow {
  amount: number;
  payment_mode: 'cash' | 'upi' | 'card' | 'netbanking' | 'cheque' | 'advance' | 'other';
  reference_number?: string;
  upi_app?: string;
  bank_name?: string;
  notes?: string;
}

export async function recordInvoicePayments(invoiceId: string, payments: PaymentRow[]) {
  return safeFetch<any>(`${BASE_URL}/billing/invoices/${invoiceId}/payments/batch`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ payments }),
  });
}

export interface SettlementResult {
  allocations: Array<{ invoice_id: string; invoice_number: string; amount: number }>;
  total_settled: number;
}

// Apply a lump-sum payment across a patient's outstanding invoices, oldest first (FIFO)
export async function settlePatientBalance(patientId: string, clinicId: string, payments: PaymentRow[]) {
  return safeFetch<SettlementResult>(`${BASE_URL}/billing/invoices/patient/${patientId}/settle`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ clinic_id: clinicId, payments }),
  });
}

export interface DepositRow {
  amount: number;
  payment_mode: 'cash' | 'upi' | 'card' | 'netbanking' | 'cheque' | 'other';
  reference_number?: string;
  upi_app?: string;
  bank_name?: string;
  notes?: string;
}

export interface PatientDeposit {
  id: string;
  amount: number;
  payment_mode: string;
  reference_number: string | null;
  upi_app: string | null;
  bank_name: string | null;
  notes: string | null;
  collected_by: string;
  created_at: string;
}

export interface PatientDepositsResult {
  deposits: PatientDeposit[];
  available_advance: number;
  outstanding: number;
  pending_amount: number;
}

export async function getPatientDeposits(patientId: string, clinicId: string) {
  return safeFetch<PatientDepositsResult>(`${BASE_URL}/billing/invoices/patient/${patientId}/deposits?clinic_id=${clinicId}`);
}

export async function createDeposit(patientId: string, clinicId: string, deposit: DepositRow) {
  return safeFetch<{ deposit: { id: string; amount: number; payment_mode: string }; available_advance: number }>(
    `${BASE_URL}/billing/invoices/patient/${patientId}/deposits`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ clinic_id: clinicId, ...deposit }),
    }
  );
}

export async function applyAdvanceToInvoice(patientId: string, clinicId: string, invoiceId: string, amount: number) {
  return safeFetch<any>(`${BASE_URL}/billing/invoices/patient/${patientId}/deposits/apply`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ clinic_id: clinicId, invoice_id: invoiceId, amount }),
  });
}

// Settle a freshly-created invoice's payment rows. 'advance' rows must be routed
// through applyAdvanceToInvoice (writes paired ledger entries that actually spend
// the deposit balance) — the generic payments/batch endpoint has no special handling
// for 'advance' and would otherwise record a phantom payment without touching the balance.
export async function settleNewInvoicePayments(invoiceId: string, patientId: string, clinicId: string, payments: PaymentRow[]) {
  const advanceRows = payments.filter(p => p.payment_mode === 'advance' && p.amount > 0);
  const otherRows   = payments.filter(p => p.payment_mode !== 'advance' && p.amount > 0);

  for (const row of advanceRows) {
    await applyAdvanceToInvoice(patientId, clinicId, invoiceId, row.amount);
  }
  if (otherRows.length) {
    await recordInvoicePayments(invoiceId, otherRows);
  }
}

export interface RefundRow {
  amount: number;
  refund_mode: 'cash' | 'upi' | 'card' | 'netbanking' | 'cheque' | 'other';
  payment_id?: string;
  reference_number?: string;
  upi_app?: string;
  bank_name?: string;
  reason?: string;
}

export interface InvoiceRefund {
  id: string;
  payment_id: string | null;
  amount: number;
  refund_mode: string;
  reference_number: string | null;
  upi_app: string | null;
  bank_name: string | null;
  reason: string | null;
  processed_by: string;
  created_at: string;
}

export interface InvoicePayment {
  id: string;
  amount: number;
  payment_mode: string;
  reference_number: string | null;
  upi_app: string | null;
  bank_name: string | null;
  status: 'success' | 'failed' | 'refunded';
  payment_date: string;
  refundable_amount: number;
}

export async function getInvoiceRefunds(invoiceId: string) {
  return safeFetch<InvoiceRefund[]>(`${BASE_URL}/billing/invoices/${invoiceId}/refunds`);
}

export async function getInvoicePayments(invoiceId: string) {
  return safeFetch<InvoicePayment[]>(`${BASE_URL}/billing/invoices/${invoiceId}/payments`);
}

export async function refundInvoicePayment(invoiceId: string, clinicId: string, refund: RefundRow) {
  return safeFetch<{ refund: { id: string; invoice_id: string; payment_id: string | null; amount: number; refund_mode: string }; invoice: any }>(
    `${BASE_URL}/billing/invoices/${invoiceId}/refund`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ clinic_id: clinicId, ...refund }),
    }
  );
}

export async function cancelBillingInvoice(invoiceId: string, cancelReason?: string) {
  return safeFetch<any>(`${BASE_URL}/billing/invoices/${invoiceId}/cancel`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ cancel_reason: cancelReason || null }),
  });
}

export async function getBillingInvoiceStats(clinicId: string, doctorId?: string, dateFrom?: string, dateTo?: string) {
  let url = `${BASE_URL}/billing/invoices/stats/${clinicId}`;
  const params = new URLSearchParams();
  
  if (doctorId) params.append('doctor_id', doctorId);
  if (dateFrom) params.append('date_from', dateFrom);
  if (dateTo) params.append('date_to', dateTo);
  
  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  return safeFetch(url);
}

export async function getBillingRevenueSummary(clinicId: string, days: number = 30) {
  return safeFetch<any>(`${BASE_URL}/billing/invoices/revenue/${clinicId}?days=${days}`);
}

// Add these functions to your existing /lib/api.js file

export async function getAllBillingInvoices(clinicId: string, options: {
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  doctorId?: string;
} = {}) {
  let url = `${BASE_URL}/billing/invoices`;
  
  // Add query parameters if options provided
  const queryParams = new URLSearchParams();
  queryParams.append('clinic_id', clinicId);
  
  if (options.status && options.status !== 'all') {
    queryParams.append('payment_status', options.status);
  }
  if (options.dateFrom) {
    queryParams.append('date_from', options.dateFrom);
  }
  if (options.dateTo) {
    queryParams.append('date_to', options.dateTo);
  }
  if (options.doctorId) {
    queryParams.append('doctor_id', options.doctorId);
  }
  
  if (queryParams.toString()) {
    url += `?${queryParams.toString()}`;
  }

  return safeFetch(url);
}

export async function getBillingAnalytics(clinicId: string, options: {
  startDate?: string;
  endDate?: string;
  doctorId?: string;
} = {}) {
  let url = `${BASE_URL}/billing/invoices/stats/${clinicId}`;
  
  // Add optional parameters
  const queryParams = new URLSearchParams();
  if (options.startDate) {
    queryParams.append('date_from', options.startDate);
  }
  if (options.endDate) {
    queryParams.append('date_to', options.endDate);
  }
  if (options.doctorId) {
    queryParams.append('doctor_id', options.doctorId);
  }
  
  if (queryParams.toString()) {
    url += `?${queryParams.toString()}`;
  }

  return safeFetch(url);
}

export async function getBillingSummary(clinicId: string) {
  const url = `${BASE_URL}/billing/summary/${clinicId}`;
  
  return safeFetch(url);
}

export async function getInvoiceDetails(invoiceId: string) {
  const url = `${BASE_URL}/billing/invoices/${invoiceId}`;
  
  return safeFetch(url);
}

export async function exportBillingData(clinicId: string, format = 'csv', filters: {
  dateFrom?: string;
  dateTo?: string;
  status?: string;
  doctorId?: string;
} = {}) {
  let url = `${BASE_URL}/billing/export/${clinicId}`;
  
  const queryParams = new URLSearchParams();
  queryParams.append('format', format);
  
  if (filters.dateFrom) {
    queryParams.append('dateFrom', filters.dateFrom);
  }
  if (filters.dateTo) {
    queryParams.append('dateTo', filters.dateTo);
  }
  if (filters.status && filters.status !== 'all') {
    queryParams.append('status', filters.status);
  }
  if (filters.doctorId) {
    queryParams.append('doctorId', filters.doctorId);
  }
  
  url += `?${queryParams.toString()}`;

  return safeFetch(url, {
    headers: {
      'Accept': format === 'csv' ? 'text/csv' : 'application/json'
    }
  });
}

export async function updateInvoiceStatus(invoiceId: string, status: string) {
  return safeFetch<any>(`${BASE_URL}/billing/invoices/${invoiceId}/status`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status }),
  });
}

export async function getBillingDashboardStats(clinicId: string, doctorId?: string) {
  let url = `${BASE_URL}/billing/dashboard/${clinicId}`;
  if (doctorId) url += `?doctorId=${doctorId}`;
  
  return safeFetch(url);
}

export async function getMonthlyBillingTrends(clinicId: string, months = 12) {
  const url = `${BASE_URL}/billing/trends/${clinicId}?months=${months}`;
  
  return safeFetch(url);
}

export async function getTopBillingServices(clinicId: string, limit = 10) {
  const url = `${BASE_URL}/billing/services/top/${clinicId}?limit=${limit}`;
  
  return safeFetch(url);
}

export async function deleteInvoice(invoiceId: string) {
  const url = `${BASE_URL}/billing/invoices/${invoiceId}`;
  
  return safeFetch(url, {
    method: "DELETE"
  });
}

export async function duplicateInvoice(invoiceId: string) {
  const url = `${BASE_URL}/billing/invoices/${invoiceId}/duplicate`;
  
  return safeFetch(url, {
    method: "POST"
  });
}

export async function getDoctorInfo(doctor_id: string) {
  const doctors = await safeFetch<any[]>(`${BASE_URL}/admin/doctors`);
  return doctors?.find((d) => d.id === doctor_id) ?? null;
}

// ==================================================
// DOCTORS
// ==================================================

export async function getDoctors() {
  return safeFetch<any[]>(`${BASE_URL}/doctors`)
}

export async function getDoctor(id: string) {
  return safeFetch<any>(`${BASE_URL}/doctors/${id}`)
}

// ==================================================
// CLINICS
// ==================================================

export async function getClinics() {
  return safeFetch<any[]>(`${BASE_URL}/clinics`)
}

export async function getClinic(id: string) {
  return safeFetch<any>(`${BASE_URL}/clinics/${id}`)
}

// ==================================================
// ADMIN
// ==================================================

export async function changePassword(currentPassword: string, newPassword: string) {
  return safeFetch<any>(`${BASE_URL}/auth/change-password`, {
    method: 'PUT',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export async function adminGetUsers() {
  return safeFetch<any[]>(`${BASE_URL}/admin/users`);
}
export async function adminCreateUser(data: any) {
  return safeFetch<any>(`${BASE_URL}/admin/users`, { method: 'POST', body: JSON.stringify(data) });
}
export async function adminUpdateUser(id: string, data: any) {
  return safeFetch<any>(`${BASE_URL}/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}
export async function adminResetPassword(id: string, new_password?: string) {
  return safeFetch<any>(`${BASE_URL}/admin/users/${id}/reset-password`, { method: 'POST', body: JSON.stringify({ new_password }) });
}

export async function adminGetDoctors() {
  return safeFetch<any[]>(`${BASE_URL}/admin/doctors`);
}
export async function adminCreateDoctor(data: any) {
  return safeFetch<any>(`${BASE_URL}/admin/doctors`, { method: 'POST', body: JSON.stringify(data) });
}
export async function adminUpdateDoctor(id: string, data: any) {
  return safeFetch<any>(`${BASE_URL}/admin/doctors/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export async function adminGetClinic() {
  return safeFetch<any>(`${BASE_URL}/admin/clinic`);
}
export async function adminUpdateClinic(data: any) {
  return safeFetch<any>(`${BASE_URL}/admin/clinic`, { method: 'PATCH', body: JSON.stringify(data) });
}

export async function adminGetInvoiceSettings() {
  return safeFetch<any>(`${BASE_URL}/admin/invoice-settings`);
}
export async function adminUpdateInvoiceSettings(data: any) {
  return safeFetch<any>(`${BASE_URL}/admin/invoice-settings`, { method: 'PUT', body: JSON.stringify(data) });
}

// ------------------ Platform Admin ------------------
// Platform-admin routes require a separate token issued via POST /auth/platform-login
// (which additionally demands the X-Platform-Secret header). Stored in sessionStorage
// so it dies when the tab closes — not persisted like the regular accessToken.

const PLATFORM_TOKEN_KEY = 'platformAccessToken';

export function getPlatformToken(): string | null {
  try { return sessionStorage.getItem(PLATFORM_TOKEN_KEY); } catch { return null; }
}
export function storePlatformToken(token: string) {
  try { sessionStorage.setItem(PLATFORM_TOKEN_KEY, token); } catch {}
}
export function clearPlatformToken() {
  try { sessionStorage.removeItem(PLATFORM_TOKEN_KEY); } catch {}
}

export async function platformAdminLogin(email: string, password: string, platformSecret: string) {
  await ensureBackendPortDiscovered();
  const res = await fetch(`${BASE_URL}/auth/platform-login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Platform-Secret': platformSecret,
    },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || 'Platform login failed');
  }
  const data = await res.json();
  storePlatformToken(data.accessToken);
  return data;
}

async function platformSafeFetch<T>(url: string, options?: RequestInit): Promise<T | null> {
  const token = getPlatformToken();
  return safeFetch<T>(url, {
    ...options,
    headers: {
      ...(options?.headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
}

export async function platformAdminGetTenants() {
  return platformSafeFetch<any[]>(`${BASE_URL}/platform-admin/tenants`);
}
export async function platformAdminGetTenant(id: string) {
  return platformSafeFetch<any>(`${BASE_URL}/platform-admin/tenants/${id}`);
}
export async function platformAdminCreateTenant(data: any) {
  return platformSafeFetch<any>(`${BASE_URL}/platform-admin/tenants`, { method: 'POST', body: JSON.stringify(data) });
}
export async function platformAdminUpdateTenant(id: string, data: any) {
  return platformSafeFetch<any>(`${BASE_URL}/platform-admin/tenants/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function platformAdminGetTenantLicense(id: string) {
  return platformSafeFetch<any>(`${BASE_URL}/platform-admin/tenants/${id}/license`);
}
export async function platformAdminUpdateTenantLicense(id: string, data: any) {
  return platformSafeFetch<any>(`${BASE_URL}/platform-admin/tenants/${id}/license`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function platformAdminResetTenantAdmin(id: string, data: { new_password: string; resend_email?: boolean }) {
  return platformSafeFetch<any>(`${BASE_URL}/platform-admin/tenants/${id}/reset-admin`, { method: 'POST', body: JSON.stringify(data) });
}

// ------------------ Permissions ------------------

export async function getPermissionRoleTemplates() {
  return safeFetch<any[]>(`${BASE_URL}/permissions/role-templates`);
}
export async function getUserPermissions(userId: string) {
  return safeFetch<any>(`${BASE_URL}/permissions/users/${userId}`);
}
export async function updateUserPermissions(userId: string, data: { role_template_id?: string | null; job_role?: string; overrides?: Record<string, 0 | 1> }) {
  return safeFetch<any>(`${BASE_URL}/permissions/users/${userId}`, { method: 'PUT', body: JSON.stringify(data) });
}

// ------------------ Auth ------------------

export async function loginUser(email: string, password: string, rememberMe = true) {
  const response = await safeFetch<{
    user: any;
    accessToken: string;
    refreshToken: string;
    expiresIn: string;
  }>(`${BASE_URL}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password })
  });

  if (response && response.accessToken) {
    try {
      setAuthStorage(rememberMe);
      const store = getAuthStorage();
      store.setItem('accessToken', response.accessToken);
      store.setItem('refreshToken', response.refreshToken);
    } catch (error) {
      console.error('Failed to store tokens:', error);
    }
  }

  return response;
}

// Refresh access token
// Singleton lock: if a refresh is already in-flight, all concurrent callers
// wait for the same promise instead of each spawning their own refresh request.
let _refreshInFlight: Promise<boolean> | null = null;

export async function refreshAccessToken(): Promise<boolean> {
  if (_refreshInFlight) return _refreshInFlight;

  _refreshInFlight = (async () => {
    try {
      const store = getAuthStorage();
      const refreshToken = store.getItem('refreshToken');
      if (!refreshToken) return false;

      const response = await fetch(`${BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        store.setItem('accessToken', data.accessToken);
        if (data.refreshToken) store.setItem('refreshToken', data.refreshToken);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    } finally {
      _refreshInFlight = null;
    }
  })();

  return _refreshInFlight;
}

// Logout and clear tokens
export function logoutUser() {
  clearAuthStorage();
}

// Request password reset (sends no email; backend returns generic message)
export async function requestPasswordReset(email: string) {
  return safeFetch<{ message: string }>(`${BASE_URL}/auth/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
}

// Submit sign-up / contact request (full name, phone, optional email)
export async function submitSignupRequest(data: { full_name: string; phone: string; email?: string }) {
  return safeFetch<{ message: string; id: string }>(`${BASE_URL}/auth/signup-request`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

// Reset password with token from email/link
export async function resetPassword(token: string, newPassword: string) {
  return safeFetch<{ message: string }>(`${BASE_URL}/auth/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, new_password: newPassword }),
  });
}

// ==================================================
// DRUGS
// ==================================================

export interface DrugSearchResult {
  id: number;
  brand_name: string;
  composition_raw: string | null;
  form: string;
  route: string;
  pack_size: string | null;
  is_rx_required: boolean | number;
  default_dosage: string | null;
  default_taken_when: string | null;
  default_duration: string | null;
  default_notes: string | null;
  custom_dosage: string | null;
  custom_taken_when: string | null;
  custom_duration: string | null;
  custom_notes: string | null;
  is_favourite: boolean | number;
  usage_count: number;
}

export async function searchDrugs(query: string, _doctorId?: string): Promise<DrugSearchResult[]> {
  const response = await safeFetch<DrugSearchResult[]>(
    `${BASE_URL}/drugs/search?q=${encodeURIComponent(query)}`
  );
  return response || [];
}

export interface DrugMolecule {
  drug_molecule_id: number;
  molecule_id: number;
  strength: string | null;
  is_primary: boolean | number;
  molecule_name: string;
  drug_class: string | null;
  schedule: string | null;
  dose_value: number | null;
  dose_unit: string | null;
  per_volume_value: number | null;
  per_volume_unit: string | null;
  strength_type: string | null;
  is_strength_parsed: boolean | number;
}

export interface DrugDetails extends DrugSearchResult {
  molecules: DrugMolecule[];
}

export async function getDrugDetails(drugId: number | string): Promise<DrugDetails | null> {
  return safeFetch<DrugDetails | null>(`${BASE_URL}/drugs/${encodeURIComponent(drugId)}`);
}

export interface PedsDosing {
  id: number;
  molecule_id: number;
  molecule_name: string;
  dose_per_kg: number;
  dose_per_kg_max: number | null;  // null = exact dose, not a range
  dose_basis: 'per_dose' | 'per_day';
  max_single_dose: number | null;
  max_daily_dose: number | null;
  dose_unit: string;               // 'mg' | 'mcg'
  doses_per_day: number;
  frequency_label: string;         // 'OD' | 'BD' | 'TDS' | 'QID'
  route: string;
  min_age_months: number | null;
  max_age_months: number | null;
  min_weight_kg: number | null;
  max_weight_kg: number | null;
  indication: string | null;
  special_notes: string | null;
  source: 'IAP' | 'BNFc' | 'WHO' | 'DCGI';
  in_range: boolean;
}

export async function getDrugPedsDosing(
  drugId: number | string,
  moleculeId: number,
  weightKg?: number
): Promise<PedsDosing[]> {
  const params = new URLSearchParams({ moleculeId: String(moleculeId) });
  if (weightKg != null && weightKg > 0) params.set('weightKg', String(weightKg));
  return safeFetch<PedsDosing[]>(`${BASE_URL}/drugs/${encodeURIComponent(drugId)}/peds-dosing?${params}`) ?? [];
}

export async function recordDrugUsage(drugId: number, doctorId: string, tenantId: string): Promise<void> {
  try {
    await safeFetch<void>(`${BASE_URL}/drugs/${encodeURIComponent(drugId)}/usage`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ doctorId, tenantId }),
    });
  } catch {
    // Silently ignore all errors
  }
}

export async function toggleDrugFavourite(drugId: string, doctorId: string): Promise<void> {
  await safeFetch<void>(`${BASE_URL}/drugs/${encodeURIComponent(drugId)}/favourite`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ doctorId }),
  });
}

export async function checkDrugInteractions(moleculeIds: string[]): Promise<any[]> {
  const response = await safeFetch<{ interactions: any[] }>(`${BASE_URL}/drugs/interactions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ moleculeIds }),
  });
  return response?.interactions || [];
}

// ------------------ FrontDesk Patient Page ------------------

export async function getPatientPrescriptions(
  patientId: string,
  options?: { from?: string; to?: string }
): Promise<any[]> {
  const params = new URLSearchParams();
  if (options?.from) params.append('from', options.from);
  if (options?.to)   params.append('to', options.to);
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await safeFetch<any[]>(
    `${BASE_URL}/prescriptions/patient/${patientId}${query}`
  );
  return Array.isArray(response) ? response : [];
}

export async function getPatientInvoices(
  patientId: string,
  options?: { from?: string; to?: string }
): Promise<any[]> {
  const params = new URLSearchParams();
  if (options?.from) params.append('from', options.from);
  if (options?.to)   params.append('to', options.to);
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await safeFetch<any[]>(
    `${BASE_URL}/billing/invoices/patient/${patientId}${query}`
  );
  if (Array.isArray(response)) return response;
  if (response && Array.isArray((response as any).invoices))
    return (response as any).invoices;
  return [];
}

export function getSixMonthsAgoDate(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 6);
  return d.toISOString().split('T')[0];
}